import { BusinessObjectBase } from './base';
import { tenantAssignmentSchema } from '@schemas/business-objects/tenant-assignment';
import type { Tenant } from './tenant';
import type { User } from './user';
import type { Id } from '../base-types';

export interface TenantAssignmentData {
  readonly id: Id;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly userId: Id;
  readonly user?: User;
}

export interface TenantAssignment extends Readonly<TenantAssignmentData> {}
type TenantAssignmentCreateWithChildrenData = Partial<TenantAssignmentData>;

export class TenantAssignment extends BusinessObjectBase {
  static readonly entityName = 'TenantAssignment' as const;
  static readonly plural = 'tenant-assignments';
  static schema = tenantAssignmentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TenantAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TenantAssignment(data));
  }

  static async findOne(filter?: any): Promise<TenantAssignment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TenantAssignment(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TenantAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TenantAssignment(data);
  }

  static async create(
    data: Omit<TenantAssignmentData, 'id'>,
  ): Promise<TenantAssignment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TenantAssignment(result);
  }

  static async createMany(
    data: Omit<TenantAssignmentData, 'id'>[],
  ): Promise<TenantAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TenantAssignment(item));
  }

  static async createWithChildren(
    data: TenantAssignmentCreateWithChildrenData,
  ): Promise<TenantAssignment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new TenantAssignment(result);
  }

  static async createManyWithChildren(
    data: TenantAssignmentCreateWithChildrenData[],
  ): Promise<TenantAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TenantAssignment(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TenantAssignmentData>,
  ): Promise<TenantAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TenantAssignment(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
